'use client'

import * as React from 'react'
import { clsx } from 'clsx'
import { ChevronDownIcon } from '@radix-ui/react-icons'

import { Button, ButtonLink, ButtonLinkProps, ButtonProps } from './button'

import * as AccordionPrimitive from '@radix-ui/react-accordion'

type AccordionProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.Root
>

const Accordion = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Root>,
  AccordionProps
>(({ children, ...props }, ref) => {
  return (
    <AccordionPrimitive.Root ref={ref} {...props}>
      {children}
    </AccordionPrimitive.Root>
  )
})

type AccordionTriggerProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.AccordionTrigger
>

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.AccordionTrigger>,
  AccordionTriggerProps
>(({ children, ...props }, ref) => {
  return (
    <AccordionPrimitive.AccordionTrigger {...props} ref={ref}>
      {children}
    </AccordionPrimitive.AccordionTrigger>
  )
})

type AccordionHeaderProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.AccordionHeader
>

const AccordionHeader = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.AccordionHeader>,
  AccordionHeaderProps
>(({ children, ...props }, ref) => {
  return (
    <AccordionPrimitive.AccordionHeader {...props} ref={ref}>
      {children}
    </AccordionPrimitive.AccordionHeader>
  )
})

type AccordionContentProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.AccordionContent
>

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.AccordionContent>,
  AccordionContentProps
>(({ children, ...props }, ref) => {
  return (
    <AccordionPrimitive.AccordionContent {...props} ref={ref}>
      {children}
    </AccordionPrimitive.AccordionContent>
  )
})

type AccordionItemProps = React.ComponentPropsWithoutRef<
  typeof AccordionPrimitive.AccordionItem
>

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.AccordionItem>,
  AccordionItemProps
>(({ children, ...props }, ref) => {
  return (
    <AccordionPrimitive.AccordionItem {...props} ref={ref}>
      {children}
    </AccordionPrimitive.AccordionItem>
  )
})

type AccordionItemOpinionatedProps = Omit<
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.AccordionItem>,
  'title'
> & {
  chevron?: boolean
  contentClassName?: string
  triggerClassName?: string
  title: React.ReactNode
  ctas?: (
    | { type: 'custom'; children: React.ReactNode }
    | { type: 'button'; props: ButtonProps }
    | { type: 'link'; props: ButtonLinkProps }
  )[]
}

const AccordionItemOpinionated = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.AccordionItem>,
  AccordionItemOpinionatedProps
>(
  (
    {
      children,
      chevron = true,
      title,
      contentClassName,
      triggerClassName,
      ctas,
      ...props
    },
    ref
  ) => {
    return (
      <AccordionPrimitive.AccordionItem {...props} ref={ref}>
        <AccordionPrimitive.AccordionHeader>
          <div className="flex items-center justify-between w-full relative">
            <AccordionPrimitive.AccordionTrigger asChild>
              <Button
                unstyled
                focusStyled
                className={clsx(
                  triggerClassName,
                  'cursor-default font-medium leading-normal group w-full p-4 flex items-center enabled:hover:bg-dark-border rounded-md radix-state-open:rounded-b-none border border-dark-border-solid truncate'
                )}
              >
                {chevron && (
                  <span className="relative group-radix-state-closed:-rotate-90 rounded border border-dark-border-solid mr-2 p-1">
                    <ChevronDownIcon width={16} height={16} />
                  </span>
                )}
                {title}
              </Button>
            </AccordionPrimitive.AccordionTrigger>
            {ctas && (
              <div className="absolute right-4 flex gap-2">
                {ctas?.map((cta, index) => {
                  switch (cta.type) {
                    case 'link':
                      return (
                        <ButtonLink
                          key={index}
                          {...cta.props}
                          ref={undefined}
                        />
                      )
                    case 'button':
                      return (
                        <Button key={index} {...cta.props} ref={undefined} />
                      )
                    case 'custom':
                      return (
                        <React.Fragment key={index}>
                          {cta.children}
                        </React.Fragment>
                      )
                    default:
                      break
                  }
                })}
              </div>
            )}
          </div>
        </AccordionPrimitive.AccordionHeader>

        <AccordionPrimitive.AccordionContent
          className={clsx(
            'radix-state-open:border-x radix-state-open:rounded-b-md radix-state-open:border-b border-dark-border-solid p-4 text-sm text-dark-dim space-y-10',
            contentClassName
          )}
        >
          {children}
        </AccordionPrimitive.AccordionContent>
      </AccordionPrimitive.AccordionItem>
    )
  }
)

export {
  Accordion,
  AccordionTrigger,
  AccordionItem,
  AccordionItemOpinionated,
  AccordionHeader,
  AccordionContent,
}
